import type { ServerConfig } from "./api";

/** A lifetime the user can pick from the form. */
export interface TtlOption {
  /** Sent to the server: a Go duration, "never", or "" for the default. */
  value: string;
  label: string;
}

/** Go durations in hours, used to compare against the server's maximum. */
const CANDIDATES: ReadonlyArray<{ value: string; label: string; hours: number }> = [
  { value: "10m", label: "10 minutes", hours: 10 / 60 },
  { value: "1h", label: "1 hour", hours: 1 },
  { value: "24h", label: "1 day", hours: 24 },
  { value: "168h", label: "7 days", hours: 168 },
  { value: "720h", label: "30 days", hours: 720 },
  { value: "8760h", label: "1 year", hours: 8760 },
];

/**
 * Parses a Go duration string such as "10m0s" or "168h0m0s" into hours.
 * Returns null when it cannot be read, so the caller can fall back rather than
 * silently offering options the server will reject.
 */
export function parseGoDurationHours(value: string): number | null {
  const pattern = /(\d+(?:\.\d+)?)(ns|us|µs|ms|s|m|h)/g;
  const perHour: Record<string, number> = {
    ns: 1 / 3.6e12,
    us: 1 / 3.6e9,
    "µs": 1 / 3.6e9,
    ms: 1 / 3.6e6,
    s: 1 / 3600,
    m: 1 / 60,
    h: 1,
  };

  let total = 0;
  let matched = false;

  for (const match of value.matchAll(pattern)) {
    const amount = Number(match[1]);
    const unit = match[2];
    if (unit === undefined || Number.isNaN(amount)) continue;

    const factor = perHour[unit];
    if (factor === undefined) continue;

    total += amount * factor;
    matched = true;
  }

  return matched ? total : null;
}

/** Renders a Go duration for display, falling back to the raw string. */
export function describeDuration(value: string): string {
  const hours = parseGoDurationHours(value);
  if (hours === null) return value;

  if (hours < 1) return `${Math.round(hours * 60)} minutes`;
  if (hours < 24) return hours === 1 ? "1 hour" : `${round(hours)} hours`;
  if (hours < 24 * 7) return hours === 24 ? "1 day" : `${round(hours / 24)} days`;
  if (hours < 8760) return `${round(hours / 24)} days`;

  const years = hours / 8760;
  return years === 1 ? "1 year" : `${round(years)} years`;
}

function round(value: number): string {
  return String(Math.round(value * 10) / 10);
}

/**
 * Builds the lifetime choices this deployment can actually honour. Options the
 * server would refuse are left out, so the form cannot produce a request that
 * is certain to fail.
 */
export function ttlOptions(config: ServerConfig | null): TtlOption[] {
  const options: TtlOption[] = [
    {
      value: "",
      label: config ? `Default (${describeDuration(config.defaultTtl)})` : "Default",
    },
  ];

  const maxHours = config?.maxTtl ? parseGoDurationHours(config.maxTtl) : null;

  for (const candidate of CANDIDATES) {
    if (maxHours !== null && candidate.hours > maxHours) continue;
    options.push({ value: candidate.value, label: candidate.label });
  }

  if (config?.allowsNever) {
    options.push({ value: "never", label: "Never expires" });
  }

  return options;
}
